import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { code: "", type: "percentage", value: 0, min_order: 0, usage_limit: 0, expires_at: "", active: true };

export default function Discounts() {
  const [discounts, setDiscounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    try {
      if (editId) await api.put(`/admin/discounts/${editId}`, form);
      else await api.post("/admin/discounts", form);
      toast.success("Discount saved");
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this discount?")) return;
    await api.delete(`/admin/discounts/${id}`);
    load();
  };

  const describe = (d) => {
    if (d.type === "percentage") return `${d.value}% off`;
    if (d.type === "flat") return `${inr(d.value)} off`;
    return "Free shipping";
  };

  return (
    <div className="space-y-4" data-testid="admin-discounts-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Discounts</h1>
        <Button size="sm" className="bg-neutral-900 hover:bg-brand text-white btn-press" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="new-discount-btn">
          <Plus size={15} className="mr-1" /> New Discount
        </Button>
      </div>

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
              <th className="p-3">Code</th><th className="p-3">Benefit</th><th className="p-3">Min Order</th>
              <th className="p-3">Usage</th><th className="p-3">Expiry</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {discounts.map((d) => (
              <tr key={d.id} className="border-b border-neutral-100" data-testid={`discount-row-${d.code}`}>
                <td className="p-3 font-mono font-semibold">{d.code}</td>
                <td className="p-3">{describe(d)}</td>
                <td className="p-3">{d.min_order ? inr(d.min_order) : "—"}</td>
                <td className="p-3 font-mono text-xs">{d.used_count}{d.usage_limit ? ` / ${d.usage_limit}` : " / ∞"}</td>
                <td className="p-3 text-xs text-neutral-500">{d.expires_at || "Never"}</td>
                <td className="p-3">
                  <span className={`text-[11px] font-mono uppercase px-2 py-0.5 ${d.active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-600"}`}>
                    {d.active ? "Active" : "Off"}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setForm({ ...EMPTY, ...d }); setEditId(d.id); setOpen(true); }} data-testid={`edit-discount-${d.code}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(d.id)} data-testid={`delete-discount-${d.code}`}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {discounts.length === 0 && <p className="p-6 text-center text-sm text-neutral-500">No discounts yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined} data-testid="discount-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "New"} Discount</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="font-mono" data-testid="discount-code-input" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="flat">Flat amount</SelectItem>
                  <SelectItem value="free_shipping">Free shipping</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type !== "free_shipping" && (
              <div><Label>{form.type === "percentage" ? "Percent (%)" : "Amount (₹)"}</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} data-testid="discount-value-input" /></div>
            )}
            <div><Label>Min order (₹)</Label><Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: Number(e.target.value) })} data-testid="discount-min-input" /></div>
            <div><Label>Usage limit (0 = ∞)</Label><Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: Number(e.target.value) })} data-testid="discount-limit-input" /></div>
            <div><Label>Expiry date</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="discount-expiry-input" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="discount-active-switch" />
              Active
            </label>
          </div>
          <Button onClick={save} className="w-full mt-2 bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="discount-save-btn">Save Discount</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
